import { CostCoverageNote, type CostCoverage } from '../../components/CostCoverageNote';
import { useQuery } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { api } from '../../lib/api';

export interface AiUsageBreakdown {
  provider: 'anthropic' | 'elevenlabs';
  windowDays: number;
  activeUsers: number;
  totalUsage: number | null;
  coverage?: CostCoverage; amountExact?: string | null;
  unit: 'USD' | 'seconds';
  meanPerUser: number | null;
  lorenz: { topPct: number; usagePct: number }[];
  headroom: { capacityPct: number; users: number }[] | null;
  monthlyQuota: number | null;
  generatedAt: string;
}

interface AiUsageResponse {
  anthropic: AiUsageBreakdown | null;
  elevenlabs: AiUsageBreakdown | null;
  note?: string;
}

function fetchAiUsage(): Promise<AiUsageResponse> {
  return api.get<AiUsageResponse>('/admin/health/ai-usage');
}

export function AiUsageSection(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['health', 'ai-usage'],
    queryFn: () => fetchAiUsage(),
    refetchInterval: 5 * 60_000,
  });

  if (isLoading) {
    return (
      <div
        className="rounded-md border p-6 text-sm text-text-tertiary"
        style={{
          background: darkTheme.colors.bgSurface,
          borderColor: darkTheme.colors.borderSubtle,
        }}
      >
        Lädt …
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-md p-3 text-sm"
        style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
      >
        {error instanceof Error ? error.message : 'Fehler'}
      </div>
    );
  }

  if (data?.note) {
    return (
      <div
        className="rounded-md border p-6 text-sm text-text-tertiary"
        style={{
          background: darkTheme.colors.bgSurface,
          borderColor: darkTheme.colors.borderSubtle,
        }}
      >
        {data.note}
      </div>
    );
  }

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <UsageCard title="Anthropic (Claude)" data={data?.anthropic ?? null} />
      <UsageCard title="ElevenLabs" data={data?.elevenlabs ?? null} />
    </div>
  );
}

function UsageCard({
  title,
  data,
}: {
  title: string;
  data: AiUsageBreakdown | null;
}): JSX.Element {
  if (!data) {
    return (
      <div
        className="rounded-md border p-4 text-sm text-text-tertiary"
        style={{
          background: darkTheme.colors.bgSurface,
          borderColor: darkTheme.colors.borderSubtle,
        }}
      >
        <div className="mb-1 font-semibold text-text-primary">{title}</div>
        Keine Daten.
      </div>
    );
  }

  const top10 = data.lorenz.find((p) => p.topPct === 10);
  const top10Severity =
    top10 && top10.usagePct > 60
      ? 'crit'
      : top10 && top10.usagePct > 40
      ? 'warn'
      : 'ok';
  const severityColor =
    top10Severity === 'crit'
      ? darkTheme.colors.danger
      : top10Severity === 'warn'
      ? darkTheme.colors.warning
      : darkTheme.colors.success;

  return (
    <article
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <header className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        <span className="text-[11px] text-text-tertiary">
          letzte {data.windowDays} Tage · {data.activeUsers} User
        </span>
      </header>

      {data.provider === 'anthropic' && <><CostCoverageNote coverage={data.coverage} amountExact={data.amountExact} />{data.meanPerUser === null && <p className="mb-3 text-xs text-warning">Verteilung und Kapazität wegen unvollständiger Kostenbasis unbekannt.</p>}</>}
      {/* Verteilung */}
      <section className="mb-4">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
          Verteilung (Lorenz)
        </div>
        <div className="space-y-1.5">
          {data.lorenz.map((p) => (
            <LorenzRow
              key={p.topPct}
              topPct={p.topPct}
              usagePct={p.usagePct}
              isTop10={p.topPct === 10}
              top10Color={severityColor}
            />
          ))}
        </div>
      </section>

      {/* Headroom */}
      {data.headroom && data.headroom.length > 0 && (
        <section className="mb-3">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
            Headroom-Projektion
          </div>
          <div className="space-y-1 text-sm">
            {data.headroom.map((h) => (
              <div key={h.capacityPct} className="flex justify-between">
                <span className="text-text-secondary">
                  {h.capacityPct}% Kontingent
                </span>
                <span className="tabular text-text-primary">
                  ≈ {h.users.toLocaleString('de-DE')} aktive User
                </span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[10px] text-text-tertiary">
            Aktuell aktiv: <span className="tabular text-text-primary">{data.activeUsers}</span>
            {data.monthlyQuota && data.headroom[2] && (
              <>
                {' '}={' '}
                {Math.round((data.activeUsers / data.headroom[2].users) * 100)}% Kapazität
              </>
            )}
          </p>
        </section>
      )}

      {/* Footer */}
      <footer
        className="border-t pt-2 text-[10px] text-text-tertiary"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      >
        Σ {formatUnit(data.totalUsage, data.unit)} ·{' '}
        ⌀ {formatUnit(data.meanPerUser, data.unit)} pro User
        {data.monthlyQuota && (
          <> · Budget {formatUnit(data.monthlyQuota, data.unit)}</>
        )}
        <br />
        ⚠ Berechnung mit Mittelwert. Bei ungleicher Verteilung (Top-Konzentration)
        wird Kapazität früher erreicht.
      </footer>
    </article>
  );
}

function LorenzRow({
  topPct,
  usagePct,
  isTop10,
  top10Color,
}: {
  topPct: number;
  usagePct: number;
  isTop10: boolean;
  top10Color: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-16 text-text-secondary">Top {topPct}%</span>
      <div
        className="relative h-2 flex-1 overflow-hidden rounded"
        style={{ background: darkTheme.colors.bgElevated }}
      >
        <div
          className="h-full rounded"
          style={{
            width: `${Math.min(100, usagePct)}%`,
            background: isTop10 ? top10Color : darkTheme.colors.accent,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
      <span className="w-12 text-right tabular text-text-primary">
        {usagePct.toFixed(1)}%
      </span>
    </div>
  );
}

function formatUnit(value: number | null, unit: 'USD' | 'seconds'): string {
  if (value === null) return 'unbekannt';
  if (unit === 'USD') return `$${value.toFixed(2)}`;
  // seconds → h:m
  const m = Math.floor(value / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}h ${mm}m`;
}
